import { useContext } from "react";
import { useSelector } from "react-redux";

import {
  FRAME,
  GROUP_WIDGET,
  SHAPE,
  TEXT,
  TYPE_INFOGRAPHIC,
  TYPE_PROPOSAL,
  PRICING_TABLE,
  TIMELINE,
  SCOPE_OF_WORK,
  TABLE,
  MAP,
  PICTURE,
  SVG,
  VIDEO,
  UPLOAD,
  YOUTUBE,
  COLLAGE,
  CHART,
  CALLOUT,
} from "../constants/editor";
import { EditorContext } from "../containers/editor/EditorLayout";
import {
  getCssTransformObj,
  getCssTransformObjToString,
  getRandomString,
  replaceDuplicateMfIdsFromText,
} from "../_helpers/utils";
import useClipboard from "./useClipboard";
import useSelectable from "./useSelectable";
import useUploadInsertOutsideImage from "./useUploadInsertOutsideImage";
import useWidgetHandler from "./useWidgetHandler";
import useReplaceDuplicateShapeId from "./useReplaceDuplicateShapeId";
import useUpdatePageDuration from "./useUpdatePageDuration";
import { EditorPanelContext } from "../containers/editor/EditorPanel";
import useReplaceScopeOfWorkIds from "./useReplaceScopeOfWorkIds";

const usePasteWidget = () => {
  let { pageNodes, metadata, widgets, updateMetadata, updateWidgets, dimension, blockNodes, documentType } =
    useContext(EditorContext);
  let { keyBoardEvent, updateKeyBoardEvent } = useContext(EditorPanelContext);

  // used global states
  const companyInfo = useSelector(state => state?.auth?.company);
  const documentDetails = useSelector(state => state?.document?.documentDetails?.data);

  // used custom Hooks
  const { toggleEventHandlers: toggleWidgetEventHandlers } = useWidgetHandler();
  const { hideAllWidgetSelection } = useSelectable();
  const { read: readFromClipboard } = useClipboard();
  const { uploadOutsideImage } = useUploadInsertOutsideImage();
  const { replaceDuplicateIdFromSVG, repalceDuplicateIdFromFrame } = useReplaceDuplicateShapeId();
  const replaceDuplicateIdFromScopeOfWork = useReplaceScopeOfWorkIds();

  const getNewPos = (widgetWidth, widgetHeight) => {
    let left, top;
    let parentPos = document.getElementById(metadata.activeBlockId).getBoundingClientRect();
    let zoomval = 100 / parseFloat(dimension.zoom);

    // allignment with respect to active block for single widget
    left = (parentPos.width / 2) * zoomval - widgetWidth / 2;
    top = (parentPos.height / 2) * zoomval - widgetHeight / 2;

    return { left: left, top: top };
  };

  const checkIsAssetPresent = () => {
    let isPricingTable = false;
    let isTimeline = false;
    let isCallOut = false;
    widgets.forEach(widget => {
      if (widget.data["data-asset-type"] === PRICING_TABLE) {
        isPricingTable = true;
      }

      if (widget.data["data-asset-type"] === TIMELINE) {
        isTimeline = true;
      }
    });

    if (
      document.querySelectorAll(`#${metadata.activePageId} .dhp-root-widget[data-asset-type="${CALLOUT}"]`).length > 0
    )
      isCallOut = true;

    return { isPricingTable, isTimeline, isCallOut };
  };

  const getGroupScaleMeta = ({ widgets, destBlockNode }) => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    widgets.forEach(w => {
      const t = getCssTransformObj({
        transform: w.style.transform,
        exclude: ["rotate", "scale"],
      });

      const x = parseFloat(t.translate.x) || 0;
      const y = parseFloat(t.translate.y) || 0;
      const width = parseFloat(w.style.width);
      const height = parseFloat(w.style.height);

      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x + width);
      maxY = Math.max(maxY, y + height);
    });

    const groupWidth = maxX - minX;
    const groupHeight = maxY - minY;

    const destWidth = parseFloat(destBlockNode.style.width);
    const destHeight = parseFloat(destBlockNode.style.height);

    const scale = Math.min(
      destWidth / groupWidth,
      destHeight / groupHeight,
      1 // never upscale
    );

    return {
      minX,
      minY,
      groupWidth,
      groupHeight,
      scale,
      offsetX: (destWidth - groupWidth * scale) / 2,
      offsetY: (destHeight - groupHeight * scale) / 2,
    };
  };

  const getScaledWidgetTransform = ({ widgetTransform, groupMeta }) => {
    const t = getCssTransformObj({
      transform: widgetTransform,
      exclude: ["rotate", "scale"],
    });

    const x = parseFloat(t.translate.x) || 0;
    const y = parseFloat(t.translate.y) || 0;

    const newX = groupMeta.offsetX + (x - groupMeta.minX) * groupMeta.scale;

    const newY = groupMeta.offsetY + (y - groupMeta.minY) * groupMeta.scale;

    return getCssTransformObj({
      transform: widgetTransform,
      translateX: `${newX}px`,
      translateY: `${newY}px`,
    });
  };

  const scaleTextWidget = (widgetEl, scale) => {
    const textNode = widgetEl.querySelector(".dhp-widget-inner");
    if (!textNode) return;

    // FONT SIZE
    const fontSize = parseFloat(textNode.style.fontSize);
    if (!isNaN(fontSize)) {
      textNode.style.fontSize = `${fontSize * scale}px`;
    }

    // LINE HEIGHT (number or px)
    const lineHeight = textNode.style.lineHeight;
    if (lineHeight) {
      if (lineHeight.includes("px")) {
        textNode.style.lineHeight = `${parseFloat(lineHeight) * scale}px`;
      }
      // if it's unitless (like 1.4), KEEP IT AS IS
    }

    // LETTER SPACING
    const letterSpacing = textNode.style.letterSpacing;
    if (letterSpacing && letterSpacing !== "0px") {
      textNode.style.letterSpacing = `${parseFloat(letterSpacing) * scale}px`;
    }

    // WIDTH (important for text wrapping)
    const width = parseFloat(textNode.style.width);
    if (!isNaN(width)) {
      textNode.style.width = `${width * scale}px`;
    }
  };

  const scaleCroppedPicture = (tempDOM, scale, newWidth, newHeight) => {
    const flippable = tempDOM.querySelector(".flippable");
    const inner = tempDOM.querySelector(".dhp-widget-inner");

    /* -----------------------------
     1. Update FLIPPABLE size
  ------------------------------ */
    if (flippable) {
      flippable.style.width = `${newWidth}px`;
      flippable.style.height = `${newHeight}px`;
    }

    /* -----------------------------
     2. Update INNER scale ONLY
     translate MUST remain 0,0
  ------------------------------ */
    if (inner) {
      const transform = inner.style.transform || "";

      // extract existing scale
      const scaleMatch = transform.match(/scale\(([^,]+),\s*([^)]+)\)/);

      const sx = scaleMatch ? parseFloat(scaleMatch[1]) : 1;
      const sy = scaleMatch ? parseFloat(scaleMatch[2]) : 1;

      const newSx = sx * scale;
      const newSy = sy * scale;

      inner.style.transformOrigin = "left top";
      inner.style.transform = `translate(0px, 0px) scale(${newSx}, ${newSy})`;
    }
  };

  const scaleCroppedPictureInGroup = (tempDOM, groupScale) => {
    const flippable = tempDOM.querySelector(".flippable");
    const inner = tempDOM.querySelector(".dhp-widget-inner");

    if (!flippable || !inner) return;

    /* -----------------------------
     1️⃣ SCALE FLIPPABLE (px only)
  ------------------------------ */

    flippable.style.width = parseFloat(flippable.style.width) * groupScale + "px";

    flippable.style.height = parseFloat(flippable.style.height) * groupScale + "px";

    /* -----------------------------
     2️⃣ SCALE INNER TRANSFORM
     (THIS IS THE KEY FIX)
  ------------------------------ */

    const transform = inner.style.transform || "";
    const scaleMatch = transform.match(/scale\(([^,]+),\s*([^)]+)\)/);

    const oldScaleX = scaleMatch ? parseFloat(scaleMatch[1]) : 1;
    const oldScaleY = scaleMatch ? parseFloat(scaleMatch[2]) : 1;

    const newScaleX = oldScaleX * groupScale;
    const newScaleY = oldScaleY * groupScale;

    inner.style.transformOrigin = "left top";
    inner.style.transform = `translate(0px, 0px) scale(${newScaleX}, ${newScaleY})`;
  };

  const scaleDownNormalImageInner = (tempDOM, scale) => {
    const flippable = tempDOM.querySelector(".flippable");
    const inner = tempDOM.querySelector(".dhp-widget-inner");

    if (!flippable || !inner) return;

    /* -----------------------------
     1️⃣ SCALE FLIPPABLE
  ----------------------------- */

    flippable.style.width = `${parseFloat(flippable.style.width) * scale}px`;

    flippable.style.height = `${parseFloat(flippable.style.height) * scale}px`;

    /* -----------------------------
     2️⃣ SCALE INNER SIZE
  ----------------------------- */

    inner.style.width = `${parseFloat(inner.style.width) * scale}px`;

    inner.style.height = `${parseFloat(inner.style.height) * scale}px`;

    /* -----------------------------
     3️⃣ SCALE TRANSLATE ONLY
  ----------------------------- */

    const transform = inner.style.transform || "";
    const match = transform.match(/translate\(([-\d.]+)px,\s*([-\d.]+)px\)/);

    let tx = 0;
    let ty = 0;

    if (match) {
      tx = parseFloat(match[1]) * scale;
      ty = parseFloat(match[2]) * scale;
    }

    inner.style.transform = `translate(${tx}px, ${ty}px) scale(1,1)`;

    /* -----------------------------
     4️⃣ SCALE DATA-CROP
  ----------------------------- */

    if (inner.dataset.crop) {
      const crop = JSON.parse(inner.dataset.crop);

      crop.n *= scale;
      crop.s *= scale;
      crop.w *= scale;
      crop.e *= scale;

      inner.dataset.crop = JSON.stringify(crop);
    }
  };

  const scaleDownCollageInner = (tempDOM, scale) => {
    const mainContent = tempDOM.querySelector(".main-content");
    if (mainContent && mainContent.style.minHeight) {
      mainContent.style.minHeight = parseFloat(mainContent.style.minHeight) * scale + "px";
    }

    const mediaGrandParents = tempDOM.querySelectorAll(".media-grand-parent");
    const mediaParents = tempDOM.querySelectorAll(".media-parent");

    mediaGrandParents.forEach(gp => {
      if (gp.style.width) gp.style.width = parseFloat(gp.style.width) * scale + "px";
      if (gp.style.height) gp.style.height = parseFloat(gp.style.height) * scale + "px";
    });

    mediaParents.forEach(parent => {
      /* -----------------------------
       1️⃣ WIDTH / HEIGHT
    ----------------------------- */
      if (parent.style.width) parent.style.width = parseFloat(parent.style.width) * scale + "px";
      if (parent.style.height) parent.style.height = parseFloat(parent.style.height) * scale + "px";

      /* -----------------------------
       2️⃣ TRANSLATE SCALE
    ----------------------------- */
      const transform = parent.style.transform || "";
      const match = transform.match(/translate\(([-\d.]+)px,\s*([-\d.]+)px\)/);

      let tx = 0,
        ty = 0;

      if (match) {
        tx = parseFloat(match[1]) * scale;
        ty = parseFloat(match[2]) * scale;
      }

      parent.style.transform = `translate(${tx}px, ${ty}px) scale(1,1)`;

      /* -----------------------------
       3️⃣ DATA-CROP SCALE
    ----------------------------- */
      if (parent.dataset.crop) {
        const crop = JSON.parse(parent.dataset.crop);

        crop.n *= scale;
        crop.s *= scale;
        crop.w *= scale;
        crop.e *= scale;

        parent.dataset.crop = JSON.stringify(crop);
      }
    });
  };

  const scaleChartWidget = (widgetEl, scale) => {
    const inner = widgetEl.querySelector(".dhp-widget-inner");
    const canvas = inner?.querySelector("canvas");

    if (!inner || !canvas) return;

    /* ----------------------------------
     1. Scale CANVAS CSS SIZE
     (visual size)
  ---------------------------------- */

    const cssWidth = parseFloat(canvas.style.width);
    const cssHeight = parseFloat(canvas.style.height);

    if (!isNaN(cssWidth)) {
      canvas.style.width = `${cssWidth * scale}px`;
    }

    if (!isNaN(cssHeight)) {
      canvas.style.height = `${cssHeight * scale}px`;
    }

    /* ----------------------------------
     2. Scale CANVAS BUFFER SIZE
     (prevents blur)
  ---------------------------------- */

    const bufferWidth = parseFloat(canvas.getAttribute("width"));
    const bufferHeight = parseFloat(canvas.getAttribute("height"));

    if (!isNaN(bufferWidth)) {
      canvas.setAttribute("width", bufferWidth * scale);
    }

    if (!isNaN(bufferHeight)) {
      canvas.setAttribute("height", bufferHeight * scale);
    }

    /* ----------------------------------
     3. Inner must remain unchanged
  ---------------------------------- */
    inner.style.transform = "scale(1)";
    inner.style.transformOrigin = "left top";
  };

  const scaleElements = (clipboardWidgetObj, idx, groupMeta) => {
    // IMPORTANT: scale width & height (bake scale)
    clipboardWidgetObj[idx].style.width = `${parseFloat(clipboardWidgetObj[idx].style.width) * groupMeta.scale}px`;

    clipboardWidgetObj[idx].style.height = `${parseFloat(clipboardWidgetObj[idx].style.height) * groupMeta.scale}px`;

    /* TEXT widget  */
    if (clipboardWidgetObj[idx].data["data-asset-type"] === TEXT) {
      const tempDOM = document.createElement("div");
      tempDOM.innerHTML = clipboardWidgetObj[idx].innerHTML;

      scaleTextWidget(tempDOM, groupMeta.scale);

      clipboardWidgetObj[idx].innerHTML = tempDOM.innerHTML;
    }

    /* Map and TABLES type widgets */
    if ([MAP, TABLE, SCOPE_OF_WORK].includes(clipboardWidgetObj[idx].data["data-asset-type"])) {
      const tempDOM = document.createElement("div");
      tempDOM.innerHTML = clipboardWidgetObj[idx].innerHTML;

      const inner = tempDOM.querySelector(".dhp-widget-inner");

      if (inner) {
        const transform = inner.style.transform || "";
        const match = transform.match(/scale\(([^)]+)\)/);

        const oldScale = match ? parseFloat(match[1]) : 1;
        const newScale = oldScale * groupMeta.scale;

        inner.style.transformOrigin = "left top";
        inner.style.transform = `scale(${newScale})`;
      }

      clipboardWidgetObj[idx].innerHTML = tempDOM.innerHTML;
    }

    /* CHART widget */
    if (clipboardWidgetObj[idx].data["data-asset-type"] === CHART) {
      const tempDOM = document.createElement("div");
      tempDOM.innerHTML = clipboardWidgetObj[idx].innerHTML;

      scaleChartWidget(tempDOM, groupMeta.scale);

      clipboardWidgetObj[idx].innerHTML = tempDOM.innerHTML;
    }

    /* 8 handler CROPPED PICTURE / Uploaded PICTURE / VIDEO / Uploaded VIDEO widget fix */
    if (
      [PICTURE, VIDEO, UPLOAD].includes(clipboardWidgetObj[idx].data["data-asset-type"]) &&
      clipboardWidgetObj[idx].data["data-file-type"] !== SVG &&
      clipboardWidgetObj[idx].data["data-source"] !== YOUTUBE &&
      (clipboardWidgetObj[idx].data["data-crop-to-shape"] === "false" ||
        !clipboardWidgetObj[idx].data["data-crop-to-shape"])
    ) {
      const tempDOM = document.createElement("div");
      tempDOM.innerHTML = clipboardWidgetObj[idx].innerHTML;

      scaleDownNormalImageInner(tempDOM, groupMeta.scale);

      clipboardWidgetObj[idx].innerHTML = tempDOM.innerHTML;
    }

    /* CROPPED PICTURE / Uploaded PICTURE / VIDEO / Uploaded VIDEO widget fix */
    if (
      clipboardWidgetObj[idx].data["data-crop-to-shape"] === true ||
      clipboardWidgetObj[idx].data["data-crop-to-shape"] === "true"
    ) {
      const tempDOM = document.createElement("div");
      tempDOM.innerHTML = clipboardWidgetObj[idx].innerHTML;

      const newWidth = parseFloat(clipboardWidgetObj[idx].style.width);
      const newHeight = parseFloat(clipboardWidgetObj[idx].style.height);

      scaleCroppedPicture(tempDOM, groupMeta.scale, newWidth, newHeight);

      clipboardWidgetObj[idx].innerHTML = tempDOM.innerHTML;
    }

    /* COLLAGE */
    if (clipboardWidgetObj[idx].data["data-asset-type"] === COLLAGE) {
      const tempDOM = document.createElement("div");
      tempDOM.innerHTML = clipboardWidgetObj[idx].innerHTML;

      scaleDownCollageInner(tempDOM, groupMeta.scale);

      clipboardWidgetObj[idx].innerHTML = tempDOM.innerHTML;
    }

    /* Group */
    if (clipboardWidgetObj[idx].data["data-asset-type"] === GROUP_WIDGET) {
      const tempDOM = document.createElement("div");
      tempDOM.innerHTML = clipboardWidgetObj[idx].innerHTML;

      /* SCALE CHILD WIDGETS */
      tempDOM.querySelectorAll(".dhp-page-widget").forEach(childWidget => {
        const transform = childWidget.style.transform || "";
        const match = transform.match(/translate\(([-\d.]+)px,\s*([-\d.]+)px\)/);

        let tx = 0,
          ty = 0;

        if (match) {
          tx = parseFloat(match[1]) * groupMeta.scale;
          ty = parseFloat(match[2]) * groupMeta.scale;
        }

        childWidget.style.transform = getCssTransformObjToString({
          transformObj: getCssTransformObj({
            transform,
            translateX: `${tx}px`,
            translateY: `${ty}px`,
          }),
        });

        const assetType = childWidget.dataset.assetType;

        /* TEXT */
        if (assetType === TEXT) {
          scaleTextWidget(childWidget, groupMeta.scale);
        }

        /* MAP / TABLE / SCOPE */
        if ([MAP, TABLE, SCOPE_OF_WORK].includes(assetType)) {
          const inner = childWidget.querySelector(".dhp-widget-inner");
          if (inner) {
            const oldScale = parseFloat(inner.style.transform?.match(/scale\(([^)]+)\)/)?.[1]) || 1;

            inner.style.transformOrigin = "left top";
            inner.style.transform = `scale(${oldScale * groupMeta.scale})`;
          }
        }

        /* NORMAL IMAGE / VIDEO */
        if (
          [PICTURE, VIDEO, UPLOAD].includes(assetType) &&
          childWidget.dataset.fileType !== SVG &&
          childWidget.dataset.source !== YOUTUBE &&
          (childWidget.dataset.cropToShape === "false" || !childWidget.dataset.cropToShape)
        ) {
          scaleDownNormalImageInner(childWidget, groupMeta.scale);
        }

        /* CROP-TO-SHAPE IMAGE / VIDEO  */
        if (childWidget.dataset.cropToShape === "true" || childWidget.dataset.cropToShape === true) {
          scaleCroppedPictureInGroup(
            childWidget,
            groupMeta.scale,
            parseFloat(childWidget.style.width),
            parseFloat(childWidget.style.height)
          );
        }

        /* COLLAGE */
        if (assetType === COLLAGE) {
          scaleDownCollageInner(childWidget, groupMeta.scale);
        }
      });

      clipboardWidgetObj[idx].innerHTML = tempDOM.innerHTML;
    }
  };

  const pasteObj = async (targetLeft, targetTop) => {
    const { dataSource, dataType, data, isValidWidget } = await readFromClipboard();
    const {
      companyId,
      widgetObj: clipboardWidgetObj,
      documentId,
      docDimension,
      blockHeight,
      clipboardAction,
    } = data ?? {
      companyId: null,
      widgetObj: null,
      documentId: null,
      docDimension: null,
      blockHeight: null,
      clipboardAction: null,
    };

    let targetWidgetIndex;
    let updatedwidgetArray = [];
    let updatedWidgetId = [];
    let updatedWidgetAssetType = [];
    let childIdRefs = {};

    //check if callout, timeline or proposal is already in doc
    let { isPricingTable, isTimeline, isCallOut } = checkIsAssetPresent();

    // if timeline or proposal is present and they are also in paste obj
    const hasConflict = clipboardWidgetObj.some(
      element =>
        (element.data["data-asset-type"] === TIMELINE && isTimeline) ||
        (element.data["data-asset-type"] === PRICING_TABLE && isPricingTable) ||
        (element.data["data-asset-type"] === CALLOUT && isCallOut)
    );

    // then return
    if (hasConflict) {
      return;
    }

    //Widget can be paste only under same company
    if (isValidWidget && dataSource === "dhp" && companyId === companyInfo?._id) {
      targetLeft = clipboardWidgetObj.length > 1 ? 0 : targetLeft;
      targetTop = clipboardWidgetObj.length > 1 ? 0 : targetTop;

      let groupMeta = null;

      if (
        ([TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType) &&
          blockHeight &&
          parseFloat(blockNodes[metadata.activeBlockIdx].style.height) < parseFloat(blockHeight)) ||
        (documentId !== documentDetails.id &&
          dimension.width !== docDimension.width &&
          dimension.height !== docDimension.height)
      ) {
        groupMeta = getGroupScaleMeta({
          widgets: clipboardWidgetObj,
          destBlockNode: blockNodes[metadata.activeBlockIdx],
        });
      }

      clipboardWidgetObj.forEach((element, idx) => {
        if (metadata.activeWidgetId.length === 1) {
          let id = metadata.activeWidgetId[0];
          let isGroupWidget = document.getElementById(id).closest(".dhp-page-group");
          let targetId = isGroupWidget
            ? document.getElementById(id).closest(".dhp-root-widget").getAttribute("id")
            : id;
          targetWidgetIndex = widgets.findIndex(widget => widget.id === targetId);
        }
        let clipBordObjAssetType = clipboardWidgetObj[idx].data["data-asset-type"];
        let newWidgetId =
          clipBordObjAssetType !== GROUP_WIDGET
            ? "dhp-widget-" + getRandomString(8)
            : "dhp-group-" + getRandomString(8);
        let widgetTransform =
          targetWidgetIndex && clipboardWidgetObj[idx].blockId === metadata.activeBlockId && clipboardAction !== "cut"
            ? widgets[targetWidgetIndex].style.transform
            : clipboardWidgetObj[idx].style.transform;
        let widgettranslateValue = getCssTransformObj({ transform: widgetTransform, exclude: ["rotate", "scale"] });
        let delta = clipboardWidgetObj[idx].blockId === metadata.activeBlockId && clipboardAction !== "cut" ? 25 : 0;
        let widgetTransformObj;

        // Copy paste item in diff doc
        if (documentId !== documentDetails.id) {
          let sourceHeight = blockHeight ? parseFloat(blockHeight) : docDimension.height;
          let destHieght = [TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType)
            ? parseFloat(blockNodes[metadata.activeBlockIdx].style.height)
            : dimension.height;

          // if diff doc but page or block size is same -> exact same position
          if (dimension.width === docDimension.width && sourceHeight === destHieght) {
            widgetTransformObj = getCssTransformObj({
              transform: widgetTransform,
              translateX: `${targetLeft ? targetLeft : parseFloat(widgettranslateValue.translate.x) + delta}px`,
              translateY: `${targetTop ? targetTop : parseFloat(widgettranslateValue.translate.y) + delta}px`,
            });
          }
          // if diff doc but page or block size is not same
          else {
            // if diff doc but page or block size is bigger than source doc page or block size -> exact same position
            if (dimension.width >= docDimension.width && destHieght >= sourceHeight) {
              widgetTransformObj = getCssTransformObj({
                transform: widgetTransform,
                translateX: `${targetLeft ? targetLeft : parseFloat(widgettranslateValue.translate.x) + delta}px`,
                translateY: `${targetTop ? targetTop : parseFloat(widgettranslateValue.translate.y) + delta}px`,
              });
            }
            // if diff doc but page or block size is smaller than source doc page or block size -> scale down and middle position
            else {
              // Apply group-based scaled transform
              widgetTransformObj = getScaledWidgetTransform({
                widgetTransform,
                groupMeta,
              });
              scaleElements(clipboardWidgetObj, idx, groupMeta);
            }
          }
        }
        // in Same Document
        else {
          // for block type document - same doc but diff block size (diff height)
          if (
            [TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType) &&
            blockHeight &&
            parseFloat(blockNodes[metadata.activeBlockIdx].style.height) !== parseFloat(blockHeight)
          ) {
            // if destination block height is bigger than source block height -> exact same position
            if (parseFloat(blockNodes[metadata.activeBlockIdx].style.height) > parseFloat(blockHeight)) {
              widgetTransformObj = getCssTransformObj({
                transform: widgetTransform,
                translateX: `${targetLeft ? targetLeft : parseFloat(widgettranslateValue.translate.x) + delta}px`,
                translateY: `${targetTop ? targetTop : parseFloat(widgettranslateValue.translate.y) + delta}px`,
              });
            }
            // else if destination block height is smaller than source block height -> scale down and middle position
            else {
              if (groupMeta) {
                // Apply group-based scaled transform
                widgetTransformObj = getScaledWidgetTransform({
                  widgetTransform,
                  groupMeta,
                });
                scaleElements(clipboardWidgetObj, idx, groupMeta);
              } else {
                // fallback (should rarely happen)
                let getPosition = getNewPos(
                  parseFloat(clipboardWidgetObj[idx].style.width),
                  parseFloat(clipboardWidgetObj[idx].style.height)
                );

                widgetTransformObj = getCssTransformObj({
                  transform: widgetTransform,
                  translateX: `${getPosition.left}px`,
                  translateY: `${getPosition.top}px`,
                });
              }
            }
          }
          // for Page/Block type document -  same doc same page/block size -> exact same position
          else {
            widgetTransformObj = getCssTransformObj({
              transform: widgetTransform,
              translateX: `${targetLeft ? targetLeft : parseFloat(widgettranslateValue.translate.x) + delta}px`,
              translateY: `${targetTop ? targetTop : parseFloat(widgettranslateValue.translate.y) + delta}px`,
            });
          }
        }

        let widgetTransformString = getCssTransformObjToString({ transformObj: widgetTransformObj });

        // update innerWidgtes id if a group widget is coppied
        if (clipBordObjAssetType === GROUP_WIDGET) {
          let copiedObjIneerHtml = clipboardWidgetObj[idx].innerHTML;
          let targetDOM = document.createElement("div");
          targetDOM.innerHTML = copiedObjIneerHtml;
          targetDOM.querySelectorAll(".dhp-page-widget").forEach(element => {
            const newId = "dhp-widget-" + getRandomString(8);
            childIdRefs = { ...childIdRefs, [element.id]: newId };
            element.setAttribute("id", newId);
          });
          targetDOM.querySelectorAll(".dhp-page-group").forEach(element => {
            element.setAttribute("id", "dhp-group-" + getRandomString(8));
          });

          replaceDuplicateIdFromSVG(targetDOM);
          replaceDuplicateMfIdsFromText(targetDOM);

          clipboardWidgetObj[idx].innerHTML = targetDOM.innerHTML;
        }
        // Replace duplicate id for shape use in shape border
        if (clipBordObjAssetType === SHAPE && clipboardWidgetObj[idx].data["data-border-style"] === "solid") {
          let copiedObjIneerHtml = clipboardWidgetObj[idx].innerHTML;
          let targetDOM = document.createElement("div");
          targetDOM.innerHTML = copiedObjIneerHtml;

          replaceDuplicateIdFromSVG(targetDOM, clipboardWidgetObj[idx]);

          clipboardWidgetObj[idx].innerHTML = targetDOM.innerHTML;
        }
        // Replace duplicate id from merge fields used in text widgets
        if (clipBordObjAssetType === TEXT) {
          let copiedObjIneerHtml = clipboardWidgetObj[idx].innerHTML;
          let targetDOM = document.createElement("div");
          targetDOM.innerHTML = copiedObjIneerHtml;

          replaceDuplicateMfIdsFromText(targetDOM);

          clipboardWidgetObj[idx].innerHTML = targetDOM.innerHTML;
        }
        // Replace duplicate id for frame
        if (clipBordObjAssetType === FRAME) {
          let copiedObjIneerHtml = clipboardWidgetObj[idx].innerHTML;
          let targetDOM = document.createElement("div");
          targetDOM.innerHTML = copiedObjIneerHtml;

          repalceDuplicateIdFromFrame(targetDOM, clipboardWidgetObj);

          clipboardWidgetObj[idx].innerHTML = targetDOM.innerHTML;
        }
        // Replace or concatenate the input check box id
        if (clipBordObjAssetType === SCOPE_OF_WORK) {
          let copiedObjInnerHtml = clipboardWidgetObj[idx].innerHTML;
          let targetDOM = document.createElement("div");
          targetDOM.innerHTML = copiedObjInnerHtml;

          replaceDuplicateIdFromScopeOfWork(targetDOM);

          clipboardWidgetObj[idx].innerHTML = targetDOM.innerHTML;
        }
        // callout arrow enable disable
        if (clipBordObjAssetType === CALLOUT) {
          let copiedObjInnerHtml = clipboardWidgetObj[idx].innerHTML;
          let targetDOM = document.createElement("div");
          targetDOM.innerHTML = copiedObjInnerHtml;

          targetDOM.querySelector(".icon-ui-arrow-left2").classList.remove("disable");
          targetDOM.querySelector(".icon-ui-arrow-right2").classList.remove("disable");

          // callout arrow disable based on active page position
          if (metadata.activePageIdx === 0) {
            targetDOM.querySelector(".icon-ui-arrow-left2").classList.add("disable");
          }
          if (metadata.activePageIdx === pageNodes.length - 1) {
            targetDOM.querySelector(".icon-ui-arrow-right2").classList.add("disable");
          }
          clipboardWidgetObj[idx].innerHTML = targetDOM.innerHTML;
        }

        //Modify new widget with new widget id and position
        let updatedClipboardObj = {
          ...clipboardWidgetObj[idx],
          pageId: metadata.activePageId,
          blockId: metadata.activeBlockId,
          id: newWidgetId,
          data: {
            ...clipboardWidgetObj[idx].data,
            "data-x-allignment":
              documentId === documentDetails.id
                ? (targetWidgetIndex || targetWidgetIndex === 0) &&
                  clipboardWidgetObj[idx].blockId === metadata.activeBlockId
                  ? false
                  : clipboardWidgetObj[idx].data["data-x-allignment"]
                : "center",
            "data-y-allignment":
              documentId === documentDetails.id
                ? (targetWidgetIndex || targetWidgetIndex === 0) &&
                  clipboardWidgetObj[idx].blockId === metadata.activeBlockId
                  ? false
                  : clipboardWidgetObj[idx].data["data-y-allignment"]
                : "middle",
          },
          style: { ...clipboardWidgetObj[idx].style, transform: widgetTransformString },
        };

        if (updatedClipboardObj?.groupSrcSet) {
          let newGroupSrcSet = {};
          Object.keys(updatedClipboardObj?.groupSrcSet)?.forEach(oldId => {
            if (childIdRefs[oldId])
              newGroupSrcSet = { ...newGroupSrcSet, [childIdRefs[oldId]]: updatedClipboardObj?.groupSrcSet?.[oldId] };
          });
          updatedClipboardObj.groupSrcSet = newGroupSrcSet;
        }

        updatedwidgetArray.push(updatedClipboardObj);
        updatedWidgetId.push(newWidgetId);
        updatedWidgetAssetType.push(clipboardWidgetObj[idx].data["data-asset-type"]);
      });

      //Update Context values
      updateWidgets([...widgets, ...updatedwidgetArray]);

      // Note : timeout is given for - handler is not move to new pasted widget for keyboard event (For multi selct widget)
      setTimeout(() => {
        updateMetadata({
          ...metadata,
          activeWidgetId: updatedWidgetId,
          activeWidgetType: updatedWidgetAssetType,
        });
        updateKeyBoardEvent({ ...keyBoardEvent, paste: false });
        toggleWidgetEventHandlers({ action: "SHOW" });
        hideAllWidgetSelection({ exceptions: updatedWidgetId, type: "group" });
      }, 30);
    }

    // For outside image, upload image
    if (isValidWidget && dataSource === "non_dhp" && dataType === "image/png") {
      uploadOutsideImage(data);
    }

    if (updatedWidgetAssetType == PRICING_TABLE) {
      navigator.clipboard.writeText("");
      localStorage.removeItem("ClipboardData");
    }
  };

  return { pasteObj };
};

export default usePasteWidget;
